/**
 * ============================================================================
 * SpeakerForm — Controller (client)
 * ============================================================================
 * Owns form state (react-hook-form), createFormValidatorResolver,
 * OTP orchestration (national only), IP lookup, GA blur, paint-first busy.
 * No JSX — view consumes the returned ViewModel.
 * ============================================================================
 */
"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useForm, type Control } from "react-hook-form";
import { useLeadFormValidationReset } from "@/helpers/useLeadFormValidationReset";
import { trackingDetails } from "@/api/getOtherFormDetails";
import { useOTPContext } from "@/components/sections/CommonModals/context";
import { useModalDispatch } from "@/context/ModalContext";
import {
  fetchAWSHeadersCountryCode,
  fetchIpInfoData,
} from "@/context/ip.utils";
import { StorageService } from "@/helpers/storage.service";
import { useInView } from "react-intersection-observer";
import { IModalActions } from "@/types/modal";
import { pushToDataLayerDeferred } from "@/helpers/gtag";
import { submitSpeakerForm } from "../service/speakerForm.service";
import {
  SPEAKER_INITIAL_VALUES,
  SPEAKER_SECTION_NAME,
  createSpeakerFormResolver,
  type SpeakerFormResponse,
  type SpeakerFormValues,
} from "../model/speakerForm.types";


export interface SpeakerFormViewModel {
  control: Control<SpeakerFormValues>;
  isValid: boolean;
  isLoading: boolean;
  isOTPLoading: boolean;
  formResponse: SpeakerFormResponse;
  setRefs: (node: Element | null) => void;
  handleFieldBlur: (
    fieldName: string,
    value: string,
    error: string | undefined,
  ) => void;
  handleFormSubmit: (e: FormEvent) => void;
}

export function useSpeakerFormController(): SpeakerFormViewModel {
  const {
     handleSendOTP, isOTPLoading, setShowLoader, otpVerified,
    isOTPSent,
  } = useOTPContext();
  const modalDispatch = useModalDispatch();
  const isSessionModalActive = StorageService.get("timeConnectModal");
  const [isSubmitUIBusy, setIsSubmitUIBusy] = useState(true);
  const [formResponse, setFormResponse] = useState<SpeakerFormResponse>({
    success: false,
    message: "",
  });
  const [ipStackDetails, setIpStackDetails] = useState<any>({});

  const resolver = useMemo(() => createSpeakerFormResolver(), []);

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    reset,
    setValue,
    clearErrors,
    formState: { isValid, isSubmitting },
  } = useForm<SpeakerFormValues>({
    defaultValues: SPEAKER_INITIAL_VALUES,
    mode: "onChange",
    resolver,
  });

  useLeadFormValidationReset({
    clearErrors,
    isOTPSent,
  });

  const isLoading = isSubmitUIBusy || isSubmitting;

  useEffect(() => {
    fetchAWSHeadersCountryCode().then(() => {
      fetchIpInfoData().then((d) => {
        setIpStackDetails(d);
        if (d?.location?.calling_code) {
          setValue(
            "countryCode",
            `+${d.location.calling_code || "91"}`,
            { shouldValidate: true },
          );
        }
      });
    });
  }, [setValue]);

  useEffect(() => {
    setIsSubmitUIBusy(false);
  }, []);

  const onSubmit = useCallback(
    (values: SpeakerFormValues) => {
      if (values.countryCode === "+91") {
        const cb = async () => {
          try {
            setIsSubmitUIBusy(true);
            const tracking = trackingDetails();

            const result = await submitSpeakerForm({
              ipstack: ipStackDetails,
              form: values as unknown as Record<string, unknown>,
              meta: {
                ...tracking,
                utm_medium: tracking?.utm_medium || "speaker_form",
              },
            });

            if (!result?.success) {
              setFormResponse({
                success: false,
                message: "Something went wrong!",
              });
            }
          } catch (error) {
            console.error("Error submitting form:", error);
            setFormResponse({
              success: false,
              message: "Something went wrong!",
            });
          } finally {
            setShowLoader(false);
            setIsSubmitUIBusy(false);
          }
        };

        handleSendOTP(values, cb);
      } else {
        setFormResponse({
          success: true,
          message: "Thank you for your interest!",
        });

        reset(SPEAKER_INITIAL_VALUES);

        setTimeout(() => {
          setFormResponse({ success: false, message: "" });
        }, 4000);
        setIsSubmitUIBusy(false);
      }
    },
    [handleSendOTP, ipStackDetails, reset, setShowLoader],
  );

  const handleFormSubmit = useCallback(
    async (e: FormEvent) => {
      e?.preventDefault?.();
      if (isLoading || isOTPLoading) return;

      try {
        await import("@/helpers/safeSubmit").then((m) =>
          m.submitAfterValidation({
            rhfHandleSubmit,
            startBusy: () => setIsSubmitUIBusy(true),
            onValid: async (values) => {
              setIsSubmitUIBusy(false);
              onSubmit(values);
            },
            onInvalid: () => setIsSubmitUIBusy(false),
            event: e,
          }),
        );
      } catch {
        setIsSubmitUIBusy(false);
      }
    },
    [isLoading, isOTPLoading, onSubmit, rhfHandleSubmit],
  );

  const ref = useRef<Element | null>(null);
  const { ref: formRef, inView } = useInView();
  const setRefs = useCallback(
    (node: Element | null) => {
      ref.current = node;
      formRef(node);
    },
    [formRef],
  );

  useEffect(() => {
    if (!isSessionModalActive) {
      modalDispatch({
        type: IModalActions.UpdateModal,
        data: {
          isModalForceClose: inView,
        },
      });
    }
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (otpVerified) {
      reset(SPEAKER_INITIAL_VALUES);
      setFormResponse({
        success: true,
        message: "Thank you for your interest!",
      });
      setTimeout(() => {
        setFormResponse({
          success: false,
          message: "",
        });
      }, 4000);
    }
  }, [otpVerified]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldBlur = useCallback(
    (fieldName: string, value: string, error: string | undefined) => {
      pushToDataLayerDeferred({
        event: "form_filled",
        field_name: fieldName,
        option_selected: value || "NA",
        error_message: error || "NA",
        section_name: SPEAKER_SECTION_NAME,
        page_type: "Ip-listing Page",
      });
    },
    [],
  );

  return {
    control,
    isValid,
    isLoading,
    isOTPLoading,
    formResponse,
    setRefs,
    handleFieldBlur,
    handleFormSubmit,
  };
}
